import React, { useEffect, useState } from 'react';
import { productService, Product, ProductFilters, productCategories, getGoldColorLabel } from '../../services';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';

const ProductsPage: React.FC = () => {
    const [products, setProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState<ProductFilters>({
        page: 1,
        page_size: 12,
    });
    const [totalPages, setTotalPages] = useState(1);
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [searchQuery, setSearchQuery] = useState('');
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

    useEffect(() => {
        loadProducts();
    }, [filters]);

    const loadProducts = async () => {
        setLoading(true);
        try {
            const response = await productService.getProducts(filters);
            setProducts(response.products || []);
            setTotalPages(response.total_pages || 1);
        } catch (err) {
            console.error('Error loading products:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCategoryChange = (category: string) => {
        setSelectedCategory(category);
        setFilters({ ...filters, category: category || undefined, page: 1 });
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setFilters({ ...filters, search: searchQuery || undefined, page: 1 });
    };

    const formatPrice = (price: string) => {
        return new Intl.NumberFormat('fa-IR').format(parseFloat(price));
    };

    const getKaratLabel = (karat: number) => {
        const labels: Record<number, string> = {
            18: 'عیار ۱۸',
            21: 'عیار ۲۱',
            24: 'عیار ۲۴',
        };
        return labels[karat] || `عیار ${karat}`;
    };

    return (
        <div className="p-4 lg:p-8">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 mb-1">محصولات</h1>
                    <p className="text-gray-500">لیست محصولات با قیمت لحظه‌ای</p>
                </div>

                {/* Search */}
                <form onSubmit={handleSearch} className="flex gap-2">
                    <div className="relative flex-1 lg:w-80">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="جستجوی محصول..."
                            className="input-field pr-10 h-11"
                        />
                        <svg className="w-5 h-5 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                    <button type="submit" className="btn-primary h-11 px-6">
                        جستجو
                    </button>
                    {isAdmin && (
                        <button
                            type="button"
                            onClick={() => navigate('/admin/products/new')}
                            className="btn-secondary h-11 px-6"
                        >
                            افزودن محصول
                        </button>
                    )}
                </form>
            </div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2 mb-8 overflow-x-auto pb-2">
                <button
                    onClick={() => handleCategoryChange('')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${selectedCategory === ''
                            ? 'bg-accent text-primary'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                >
                    همه
                </button>
                {productCategories.map((cat) => (
                    <button
                        key={cat.value}
                        onClick={() => handleCategoryChange(cat.value)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap flex items-center gap-2 ${selectedCategory === cat.value
                                ? 'bg-accent text-primary'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                    >
                        <span>{cat.icon}</span>
                        <span>{cat.label}</span>
                    </button>
                ))}
            </div>

            {/* Products Grid */}
            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="card">
                            <div className="skeleton h-48 mb-4" />
                            <div className="skeleton h-4 w-3/4 mb-2" />
                            <div className="skeleton h-4 w-1/2 mb-4" />
                            <div className="skeleton h-8 w-full" />
                        </div>
                    ))}
                </div>
            ) : products.length === 0 ? (
                <div className="text-center py-16">
                    <svg className="w-20 h-20 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">محصولی یافت نشد</h3>
                    <p className="text-gray-500">فیلترها را تغییر دهید یا جستجوی دیگری انجام دهید</p>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {products.map((product) => (
                            <div key={product.id} className="product-card shadow-md">
                                {/* Image */}
                                <div className="relative h-48 overflow-hidden">
                                    {product.image_urls && product.image_urls.length > 0 ? (
                                        <img
                                            src={product.image_urls[0]}
                                            alt={product.name}
                                            className="product-image w-full h-full object-cover"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                                            <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                    )}

                                    {/* Featured badge */}
                                    {product.is_featured && (
                                        <div className="absolute top-3 right-3 px-2 py-1 bg-accent text-primary text-xs font-bold rounded-lg">
                                            ویژه
                                        </div>
                                    )}

                                    {/* Karat badge */}
                                    <div className="absolute top-3 left-3 px-2 py-1 bg-primary/80 backdrop-blur-sm text-white text-xs font-medium rounded-lg">
                                        {getKaratLabel(product.karat)}
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-4">
                                    <h3 className="font-bold text-gray-900 mb-1 line-clamp-1">{product.name}</h3>
                                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{product.description}</p>

                                    {/* Details */}
                                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
                                        <div className="flex items-center gap-1">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                                            </svg>
                                            <span>{product.net_weight} گرم</span>
                                        </div>
                                        {product.gold_color && (
                                            <div className="flex items-center gap-1">
                                                <div className={`w-3 h-3 rounded-full ${product.gold_color === 'yellow' ? 'bg-yellow-400' :
                                                        product.gold_color === 'white' ? 'bg-gray-300' :
                                                            'bg-rose-300'
                                                    }`} />
                                                <span>
                                                    {getGoldColorLabel(product.gold_color)}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Price */}
                                    <div className="price-tag text-center">
                                        {product.calculated_price ? (
                                            <span>{formatPrice(product.calculated_price.total_price)} تومان</span>
                                        ) : (
                                            <span className="text-sm">برای مشاهده قیمت وارد شوید</span>
                                        )}
                                    </div>

                                    {/* Stock */}
                                    {product.stock <= 5 && product.stock > 0 && (
                                        <p className="text-xs text-orange-600 text-center mt-2">
                                            فقط {product.stock} عدد موجود
                                        </p>
                                    )}
                                    {product.stock === 0 && (
                                        <p className="text-xs text-red-600 text-center mt-2">
                                            ناموجود
                                        </p>
                                    )}
                                    {isAdmin && (
                                        <div className="flex items-center justify-between mt-4">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => navigate(`/admin/products/${product.id}/edit`)}
                                                    className="px-3 py-2 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors text-sm"
                                                >
                                                    ویرایش
                                                </button>
                                                <button
                                                    onClick={() => navigate(`/admin/products/${product.id}/images`)}
                                                    className="px-3 py-2 rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors text-sm"
                                                >
                                                    تصاویر
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center items-center gap-2 mt-8">
                            <button
                                onClick={() => setFilters({ ...filters, page: (filters.page || 1) - 1 })}
                                disabled={filters.page === 1}
                                className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>

                            <span className="px-4 py-2 text-sm text-gray-600">
                                صفحه {filters.page} از {totalPages}
                            </span>

                            <button
                                onClick={() => setFilters({ ...filters, page: (filters.page || 1) + 1 })}
                                disabled={filters.page === totalPages}
                                className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                        </div>
                    )}
                </>
            )}
            
        </div>
    );
};

export default ProductsPage;
